import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import Moment from 'moment';
import {
    debounce as _debounce,
    isEqual as _isEqual,
} from 'lodash';
import { toast } from 'react-toastify';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { DataGridPro, useGridApiRef as useApiRef } from '@mui/x-data-grid-pro';

import AddIcon from '@mui/icons-material/Add';
import FrownIcon from '@mui/icons-material/SentimentDissatisfiedSharp';
import SmileIcon from '@mui/icons-material/SentimentSatisfiedSharp';
import DownloadIcon from '@mui/icons-material/GetApp';

import CellExpand from '../../../components/xgrid/cell-expand';
import SearchBar from '../../../components/searchbar';
import { LoadingOverlay, NoRowsOverlay } from '../../../components/xgrid/overlays';

import AuditService, { fields } from '../../../data/audit-service';
import Auth, { ProfileContext } from '../../../data/auth-service';

import useWindowSize from '../../../libraries/usewindowsize';
import Helpers from '../../../libraries/helpers';
import DateTimePicker from '../../../components/styled/datetimepicker';

const useStyles = makeStyles(theme => ({
    buttonRight: {
        float: 'right',
    },
    buttonLeft: {
        float: 'left',
    },
    roleUsers: {
        display: 'inline',
    },
    fullWidth: {
        width: '100%',
    },
    linkRight: {
        float: 'right',
        marginLeft: theme.spacing(1),
    },
    datePicker: {
        maxWidth: 162,
    },
}));

const Audit = () => {
    const classes = useStyles();
    const theme = useTheme();
    const [init, setInit] = useState(false);
    const [auditData, setAuditData] = useState({ data: [] });
    const [filter, setFilter] = useState({ title: 'All', variable: 'all' });
    const [filterEl, setFilterEl] = useState(null);
    const [reportEl, setReportEl] = useState(null);
    const [format, setFormat] = useState({});
    const [search, setSearch] = useState('');
    const [downloadURL] = useState('');
    const auditAPIRef = useApiRef();
    const [loading, setLoading] = useState(0);
    const [limitReached, setLimitReached] = useState(false);
    const downloadToastRef = useRef(null);
    const { height } = useWindowSize();
    const { updateProfile, getProfile } = useContext(ProfileContext);
    const [columnWidth, setColumnWidth] = useState({});
    const [auditSortModel, setAuditSortModel] = useState([{ field: 'created', sort: 'desc' }]);
    const [start, setStart] = useState(Moment().subtract(1, 'days').startOf('day'));
    const [end, setEnd] = useState(Moment().endOf('day'));
    const [sort, setSort] = useState('createddesc');
    const [limit] = useState(50);

    const auditPerms = Auth.permissions.get('Audit');

    const REPORTS = [
        { type: 'csv', name: 'CSV' },
        { type: 'html', name: 'HTML' },
        { type: 'rst', name: 'Plain text' },
    ];

    useEffect(() => {
        setColumnWidth(getProfile('audit').widths || {});
    }, [getProfile]);

    const handleColumnResize = useCallback(params => {
        setColumnWidth({
            ...columnWidth,
            [params.colDef.variable]: params.width,
        });
    }, [columnWidth]);

    useEffect(() => {
        if (!columnWidth || !Object.keys(columnWidth).length) return;

        updateProfile('audit', {
            ...getProfile('audit'),
            widths: columnWidth,
        });
    }, [updateProfile, getProfile, columnWidth]);

    const handleColumnReorder = () => {
        let order = [];
        setTimeout(() => {
            order = auditAPIRef.current.getAllColumns().reduce((a, c) => {
                a.push(c.headerName);
                return a;
            }, []);

            updateProfile('audit', {
                ...getProfile('audit'),
                columns: order,
            });
        });
    };

    const handleScroll = (p) => {
        if (!(auditData.data || []).length) return;
        // one page at a time, please
        if (loading > 0) return;
        if (limitReached) return;
        getAuditData(auditData.data.length, 'scrolled');
    };

    const getAuditData = (offset, msg) => {
        const _loading = new Date().getTime();
        setLoading(_loading);
        AuditService.getData({
            search,
            filter: filter.variable,
            sort,
            offset,
            limit,
            start,
            end
        }).then(res => {
            const d = res.data.map((v) => { return { ...v, id: Helpers.UniqueID() } });
            setLimitReached(d.length < limit);
            setAuditData(current => ({
                ...res,
                data: [...current.data, ...d],
            }));
        }).finally(() => {
            // we might be here because we got canceled by a new request
            setLoading(current => current === _loading ? 0 : current);
        });
    };

    const handleOpenReports = (e) => {
        setReportEl(e.currentTarget);
    };

    const params = () => ({
        search: search,
        filter: filter.variable,
    });

    const handleCloseReports = (e) => {
        if (REPORTS[e.currentTarget.value]) {
            const p = params();
            p.format = REPORTS[e.currentTarget.value].type;
            setFormat(p.format);
        }
        setReportEl(null);
    };

    const handleOpenFilters = (e) => {
        setFilterEl(e.currentTarget);
    };

    const handleCloseFilters = v => {
        const f = fields.find(f => f.variable === v.variable);
        if (f) {
            setFilter(f);
        } else {
            setFilter({ title: 'All', variable: 'all' });
        }
        setFilterEl(null);
    };

    const handleStartChange = start => {
        if (!start || !start.isValid()) return;
        setStart(start);
    };

    const handleEndChange = end => {
        if (!end.isValid()) return;
        setEnd(end);
    };

    const _handleSearchChange = ({ search }) => {
        setSearch(search || '');
    };

    const handleSearchChange = _debounce(_handleSearchChange, 420);

    /* eslint-disable react/display-name */
    const tableRenderers = {
        created: d => { return <div>{Moment(d.row.created).format(theme.date.long)}</div> },
        source: d => { return <div>{d.row.source}</div> },
        host: d => { return <div>{d.row.host}</div> },
        actor: d => { return <div>{d.row.actor}</div> },
        action: d => { return <CellExpand { ...{ ...d, value: d.row.action } }/> },
        subject: d => { return <CellExpand { ...{ ...d, value: d.row.subject } }/> },
        result: d => { return d.row.result === 'fail' ? <FrownIcon style={{ color: theme.palette.error.main }} /> : <SmileIcon style={{ color: theme.palette.success.main } }/> },
    };

    const auditColumns = fields.map(f => {
        return {
            ...f,
            sortable: true,
            width: columnWidth[f.variable] || 100,
            // ...(!columnWidth[f.variable] ? { flex: f.flexDefault || 1 } : {}),
            headerName: f.title,
            valueGetter: (p) => { return p.row[f.variable] },
            type: 'string',
            disableColumnMenu: true,
            hidden: false,
            renderCell: tableRenderers[f.variable],
        };
    });

    useEffect(() => {
        if (!init) {
            setInit(true);
            return;
        }
        // reset table
        setLimitReached(false);
        setAuditData({ data: [] });
        getAuditData(0, 'search changed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, filter, sort, start, end, init]);

    const handleUpdateSort = (s) => {
        if (!_isEqual(s, auditSortModel)) {
            setAuditSortModel(s);
            setSort(s.length ? s[0].field.replace('data.', '') + s[0].sort : 'createddesc');
        }
    };

    const handleCreateReport = (format) => {
        toast.info('Generating report. There will be a notification once it is ready for download.');
        AuditService.generateReport({
            sort,
            search,
            filter: filter.variable,
            format: format,
            start,
            end
        }).then(res => {
            downloadToastRef.current = toast(<React.Fragment>
                <Box display="flex" alignItems="center">
                    <Typography>Audit report ready for download</Typography>
                    <Link
                        className={classes.linkRight}
                        target="_blank"
                        download={`audit.${format === 'rst' ? 'txt' : format}`}
                        href={res}
                    >
                        <IconButton
                            aria-label="download history"
                            onClick={() => { toast.dismiss(downloadToastRef.current) }}
                            size="large"><DownloadIcon/>
                        </IconButton>
                    </Link>
                </Box>
            </React.Fragment>, {
                autoClose: 30000,
            });
        }, err => {
            console.error(err);
            toast(err);
        });
    };

    return (
        <Grid container>
            <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between">
                    <Grid display="flex">
                        <SearchBar immediate onSearch={handleSearchChange} searchservice={AuditService} style={{ width: 600 }}/>
                        <Box ml={2}>
                            <Button variant="contained" color="primary" className={classes.buttonLeft} aria-label={'filter audit by ' + filter.title} onClick={handleOpenFilters}>{filter.title}</Button>
                            <Menu
                                anchorEl={filterEl}
                                open={Boolean(filterEl)}
                                onClose={e => setFilterEl(null)}
                            >
                                <MenuItem
                                    aria-label={'filter audit by all'}
                                    value={'all'}
                                    onClick={e => handleCloseFilters('')}
                                >All</MenuItem>
                                {fields.filter(v => v.filter).map((v, i) => {
                                    return <MenuItem
                                        key={'audit-filter-' + i}
                                        aria-label={'filter audit by ' + i}
                                        value={i}
                                        onClick={e => handleCloseFilters(v)}
                                    >{v.title}</MenuItem>;
                                })}
                            </Menu>
                        </Box>
                        <Grid ml={4} container spacing={2} direction="row" wrap="nowrap">
                            <Grid item className={classes.datePicker}>
                                <DateTimePicker
                                    label="Start"
                                    aria-label="start"
                                    value={start}
                                    onChange={handleStartChange}
                                    inputFormat={theme.date.short}
                                    disableMaskedInput={true}
                                    maxDate={end}
                                    showTodayButton={true}
                                    showToolbar={true}
                                    ampm={false}/>
                            </Grid>
                            <Grid item className={classes.datePicker}>
                                <DateTimePicker
                                    clearable={true}
                                    ampm={false}
                                    aria-label="end"
                                    label="End"
                                    value={end}
                                    onChange={handleEndChange}
                                    inputFormat={theme.date.short}
                                    disableMaskedInput={true}
                                    minDate={start}
                                    showTodayButton={true}
                                    showToolbar={true}/>
                            </Grid>
                        </Grid>
                    </Grid>
                    {auditPerms.write && <Box>
                        <Link
                            className={classes.linkRight}
                            target="_blank"
                            aria-label="download"
                            download={`audit-${Moment().format('MM/DD/YY-HH:mm:ss')}.${format === 'rst' ? 'txt' : format}`}
                            style={{ display: downloadURL ? 'block' : 'none' }} href={downloadURL}><Button aria-label="download report">Download</Button></Link>
                        <Button variant="contained" className={classes.buttonRight} aria-label="create report" onClick={handleOpenReports}><AddIcon/> Report</Button>
                        <Menu
                            anchorEl={reportEl}
                            open={Boolean(reportEl)}
                            onClose={handleCloseReports}
                        >
                            {REPORTS.map((v, i) => {
                                return <MenuItem
                                    key={'audit-filter-' + i}
                                    value={i}
                                    onClick={() => { handleCreateReport(v.type) }}
                                >{v.name}</MenuItem>;
                            })}
                        </Menu>
                    </Box>}
                </Box>
                <Box mt={3} className={classes.fullWidth} height={height - 230}>
                    <DataGridPro
                        components={{
                            LoadingOverlay: LoadingOverlay,
                            NoRowsOverlay: NoRowsOverlay,
                        }}
                        apiRef={auditAPIRef}
                        rows={auditData.data}
                        columns={(() => {
                            const p = getProfile('audit');
                            let c = auditColumns.map(cc => ({ ...cc, width: columnWidth[cc.variable] || cc.width }));
                            if (p.columns && p.columns.length) {
                                c = p.columns.map(v => {
                                    for (let i = c.length; i--;) {
                                        if (v === c[i].headerName) {
                                            return c[i];
                                        }
                                    }
                                    return null;
                                });
                            }
                            return c;
                        })()}
                        sortModel={auditSortModel}
                        sortingOrder={['desc', 'asc']}
                        loading={loading > 0}
                        disableMultipleSelection
                        disableMultipleColumnsSorting
                        disableColumnFilter
                        disableColumnMenu
                        onSortModelChange={handleUpdateSort}
                        onColumnResize={handleColumnResize}
                        onColumnOrderChange={handleColumnReorder}
                        onRowsScrollEnd={handleScroll}
                    />
                </Box>
            </Grid>
        </Grid>
    );
};

export default Audit;
